import { AuthType, EmbedConfig, LogLevel, prefetch, PrefetchFeatures } from '@thoughtspot/visual-embed-sdk';
import { customCssVariables, stringOverrides, unstableCssVariables } from '@/customizations/thoughtspotCustomizations';
import { Configuration, createBearerAuthenticationConfig, ThoughtSpotRestApi } from '@thoughtspot/rest-api-sdk';
import { useBoundStore } from '@/store';

const getCurrentThoughtspotUrl = (): string => {
  return useBoundStore.getState().getCurrentThoughtspotUrl();
};

export const DEFAULT_CONFIG = {
  get host() {
    return getCurrentThoughtspotUrl();
  },
  authType: AuthType.TrustedAuthTokenCookieless,
  logLevel: !import.meta.env.PROD ? LogLevel.SILENT : LogLevel.ERROR,
} as const;

/**
 * Helper function to build the embed configuration.
 * Exported primarily for testing internal logic.
 */
export function buildEmbedConfig({
  username,
  host = DEFAULT_CONFIG.host,
}: {
  username: string;
  host?: string;
}): EmbedConfig {
  return {
    thoughtSpotHost: host,
    authType: DEFAULT_CONFIG.authType,
    logLevel: DEFAULT_CONFIG.logLevel,
    autoLogin: true,
    username,
    suppressErrorAlerts: true, // let the TS Event Emitter handle errors
    blockNonEmbedFullAppAccess: true, // Prevent users from accessing the full application or ThoughtSpot application pages
    customizations: {
      content: {
        strings: { ...stringOverrides },
      },
      style: {
        customCSS: {
          variables: { ...customCssVariables },
          rules_UNSTABLE: { ...unstableCssVariables },
        },
      },
    },
  };
}

/**
 * Creates and initializes the Visual SDK and returns the AuthEventEmitter.
 */
export function createEmbedConfig({
  username,
  host = DEFAULT_CONFIG.host,
}: {
  username: string;
  host?: string;
}): EmbedConfig {
  const embedConfig = buildEmbedConfig({ username, host });
  return embedConfig;
}

/**
 * Utility to remove trailing slash from host.
 */
export const getAdjustedHost = (host: string): string => {
  return host.endsWith('/') ? host.slice(0, -1) : host;
};

/**
 * Creates and returns the ThoughtSpot REST API client.
 */
export function createRestApiClient({
  _token,
  host = DEFAULT_CONFIG.host,
}: {
  _token: string;
  host?: string;
}): ThoughtSpotRestApi {
  const config: Configuration = createBearerAuthenticationConfig(getAdjustedHost(host), () => Promise.resolve(_token));
  const apiClient = new ThoughtSpotRestApi(config);
  return apiClient;
}

export const callPrefetch = (): void => {
  const thoughtspotUrl =
    useBoundStore.getState().getCurrentThoughtspotUrl() || 'https://alchemeranalytics.thoughtspot.cloud/';
  prefetch(thoughtspotUrl, [PrefetchFeatures.FullApp, PrefetchFeatures.LiveboardEmbed]);
};
